/**
 * Utility functions for authentication and routing
 */

/**
 * Creates a login URL with a return URL parameter
 * @param {string} returnUrl - The URL to redirect to after login
 * @returns {string} - The login URL with encoded return URL
 */
export const createLoginUrlWithReturn = (returnUrl) => {
  if (!returnUrl) {
    return "/Login";
  }

  const encodedReturnUrl = encodeURIComponent(returnUrl);
  return `/Login?Return_Url=${encodedReturnUrl}`;
};

/**
 * Creates a register URL with a return URL parameter
 * @param {string} returnUrl - The URL to redirect to after registration
 * @returns {string} - The register URL with encoded return URL
 */
export const createRegisterUrlWithReturn = (returnUrl) => {
  if (!returnUrl) {
    return "/Register";
  }

  const encodedReturnUrl = encodeURIComponent(returnUrl);
  return `/Register?Return_Url=${encodedReturnUrl}`;
};

/**
 * Redirects to login page with current path as return URL
 * @param {function} navigate - React Router navigate function
 * @param {string} currentPath - Current path to return to after login
 */
export const redirectToLoginWithReturn = (navigate, currentPath) => {
  const loginUrl = createLoginUrlWithReturn(currentPath);
  navigate(loginUrl);
};

/**
 * Checks if user has access to a route and redirects to login if not
 * @param {boolean} hasAccess - Whether user has access to the route
 * @param {function} navigate - React Router navigate function
 * @param {string} currentPath - Current path to return to after login
 * @returns {boolean} - Returns true if user has access, false otherwise
 */
export const checkAccessOrRedirectToLogin = (
  hasAccess,
  navigate,
  currentPath
) => {
  if (!hasAccess) {
    redirectToLoginWithReturn(navigate, currentPath);
    return false;
  }
  return true;
};

/**
 * Gets the return URL from current location search params
 * @param {object} location - React Router location object
 * @returns {string|null} - The decoded return URL or null if not found
 */
export const getReturnUrlFromLocation = (location) => {
  const queryParams = new URLSearchParams(location.search);
  const returnUrl = queryParams.get("Return_Url");

  if (returnUrl) {
    try {
      return decodeURIComponent(returnUrl);
    } catch (error) {
      console.error("Error decoding return URL:", error);
      return null;
    }
  }

  return null;
};
